import { useMemo, useRef, useState } from "react";
import { trpc } from "../lib/trpc";
import { parseTrNumber } from "../lib/trNumber";
import { fold } from "../lib/trSearch";
import { Icon } from "../components/ui/Icon";
import { PageFormShell } from "../components/ui/PageFormShell";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { useBulkPeriod, useCloseBulkTab, fmtH, fmtD, MAX_ROWS } from "../components/hr/bulkShared";
import "./Items.css";        // .items-page (PageFormShell) · .dt2 .cell-num · .iact
import "./Maintenance.css";  // .mt-empty (liste boş / yükleniyor durumu)
import "../components/hr/BulkEntry.css";

/**
 * TOPLU DEVAMSIZLIK BEYANI — uygulama içi SEKME. Toplu Fazla Mesai ekranının AYNASI.
 *
 * Kullanıcı listeden personelleri seçer, her satıra tarih + DEVAMSIZLIK SAATİ yazar; tek
 * kaydetle hepsi puantaja işlenir ve bordroda gün karşılığı kadar kesinti doğar.
 *
 * ── SAAT NEDEN GÜNE ÇEVRİLİYOR ──
 * Bordro kesintiyi GÜN üzerinden yapar (günlük ücret × devamsız gün). 8 saatlik bir günde
 * 3 saat devam etmemek 3/8 = 0,375 gün eder. Ekran hem ham saati hem gün karşılığını
 * gösterir; kullanıcı "6 saat girdim ama bordroda 0,75 gün yazıyor" diye şaşırmasın.
 *
 * ── YALNIZ ÇALIŞILAN GÜNE ──
 * Hafta sonu / resmî tatil / izinli / zaten tam devamsız güne saatlik devamsızlık
 * GİRİLEMEZ (sunucu reddeder). Satır tablosu bunu KAYDETMEDEN ÖNCE kırmızı gösterir:
 * `dayGrid` önizlemesi o günün puantaj satırını okur.
 *
 * ── PARA YOK ──
 * Ekranda TL yoktur. Kesinti tutarı gösterilseydi (günlük ücret × gün) kullanıcı bölmeyi
 * tersine çevirip MAAŞI bulurdu — mesai ekranındaki gerekçenin aynısı.
 */

export const BULK_ABSENCE_PATH = "/personel/devamsizlik";

interface RowState {
  /** "YYYY-MM-DD" */
  date: string;
  /** Serbest metin — TR ondalık kabul eder ("1,5"). */
  hours: string;
}

/** Durum → etiket + o güne devamsızlık girilebilir mi. Sunucudaki kuralın ekran karşılığı. */
const STATUS_META: Record<string, { label: string; workable: boolean }> = {
  present: { label: "İşte", workable: true },
  remote: { label: "Uzaktan", workable: true },
  absent: { label: "Devamsız (tam gün)", workable: false },
  leave: { label: "İzinli", workable: false },
  holiday: { label: "Resmî Tatil", workable: false },
  weekend: { label: "Hafta Sonu", workable: false },
  half_day: { label: "Yarım Gün", workable: false },
};

/** `dayGrid` önizlemesinin açılabileceği en geniş seçim — sunucudaki `.max(60)` ile AYNI. */
const PREVIEW_LIMIT = 60;

export function BulkAbsencePage() {
  const qc = trpc.useUtils();
  const back = useCloseBulkTab(BULK_ABSENCE_PATH);
  const per = useBulkPeriod();
  const { year, month } = per;

  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuper = !!meQ.data?.user?.isSuperAdmin;
  const canManage = isSuper || (meQ.data?.user?.permissions ?? []).includes("manage:hr");

  const monthQ = trpc.hr.attendance.absenceMonth.useQuery({ year, month }, { enabled: per.valid });
  const statusQ = trpc.hr.attendance.monthStatus.useQuery({ year, month }, { enabled: per.valid, staleTime: 5_000 });
  const locked = statusQ.isLoading || statusQ.data?.payrollStatus === "approved" || statusQ.data?.payrollStatus === "paid";

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) =>
    setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Map<string, RowState>>(new Map());
  const [applyDate, setApplyDate] = useState("");
  const [applyHours, setApplyHours] = useState("");

  const people = monthQ.data?.employees ?? [];

  // ── Gün önizlemesi: seçili personelin o aydaki puantaj satırları ──
  // Seçim genişse KAPANIR (uç 60 ile sınırlı) ve ekran bunu açıkça yazar.
  const pickedIds = useMemo(() => [...picked.keys()], [picked]);
  const previewOn = pickedIds.length > 0 && pickedIds.length <= PREVIEW_LIMIT;
  const gridQ = trpc.hr.attendance.dayGrid.useQuery(
    { year, month, employeeIds: pickedIds },
    { enabled: per.valid && previewOn, staleTime: 5_000 },
  );
  const gridBy = useMemo(() => {
    const m = new Map<string, { status: string; hoursWorked: number | null; absenceHours: number | null }>();
    for (const g of gridQ.data?.days ?? []) m.set(`${g.employeeId}|${g.date}`, g);
    return m;
  }, [gridQ.data]);

  const filtered = useMemo(() => {
    const qq = fold(q.trim());
    if (!qq) return people;
    return people.filter((p) => fold(p.name).includes(qq) || fold(p.code ?? "").includes(qq) || fold(p.positionTitle ?? "").includes(qq));
  }, [people, q]);

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, { date: applyDate, hours: applyHours });
      return next;
    });
  };
  const selectAllShown = () => {
    setPicked((prev) => {
      const next = new Map(prev);
      // Sıra GÖRÜNEN listeden alınır; Map ekleme sırası değil.
      for (const p of filtered) if (!next.has(p.employeeId)) next.set(p.employeeId, { date: applyDate, hours: applyHours });
      return next;
    });
  };
  const setRow = (id: string, patch: Partial<RowState>) => {
    setPicked((prev) => {
      const next = new Map(prev);
      const cur = next.get(id);
      if (cur) next.set(id, { ...cur, ...patch });
      return next;
    });
  };
  /** "Hepsine uygula" — en sık durum: aynı gün birden çok kişi gelmedi (servis, hava, vardiya). */
  const spread = () => {
    setPicked((prev) => {
      const next = new Map<string, RowState>();
      for (const [id, r] of prev) next.set(id, { date: applyDate || r.date, hours: applyHours || r.hours });
      return next;
    });
  };

  const nameOf = useMemo(() => new Map(people.map((p) => [p.employeeId, p])), [people]);
  const rows = useMemo(() => [...picked.entries()].map(([id, r]) => ({ id, ...r })), [picked]);

  const ymKey = `${year}-${String(month).padStart(2, "0")}`;
  const parsed = rows.map((r) => {
    const h = r.hours.trim() === "" ? NaN : parseTrNumber(r.hours);
    const dateOk = !!r.date && r.date.slice(0, 7) === ymKey;
    // Üst sınır 24: sunucudaki zod ile aynı. Gerçek tavan o günün çalışma saatidir ve
    // aşağıda önizlemeden okunur.
    const hoursRangeOk = Number.isFinite(h) && h >= 0 && h <= 24;
    const g = dateOk ? gridBy.get(`${r.id}|${r.date}`) : undefined;
    const meta = g ? STATUS_META[g.status] : undefined;
    const dayHours = g?.hoursWorked ?? null;
    /**
     * ÖNİZLEME "BİLİNMİYOR" OLABİLİR — ve bu bir hata değildir: o gün için henüz puantaj
     * satırı yoksa günü kaydederken MOTOR çözer. Bilinmeyeni hata saymak, ayı henüz
     * hesaplamamış kullanıcıya hiçbir satır girdirmezdi.
     */
    const known = !!g;
    /**
     * SAAT 0 = "DEVAMSIZLIĞI KALDIR" ve bu HER KOŞULDA geçerlidir (sunucu da öyle).
     *
     * Kurallar saatten bağımsız uygulansaydı şu tuzak doğardı: 5 Eylül'e 3 saat girilir,
     * sonra o güne izin onaylanır ve "Puantaj Hesapla" günü `leave`e çevirir. Kayıt ay
     * toplamında durur ama kesinti doğurmaz — ve kullanıcı onu bu ekrandan SİLEMEZ,
     * üstelik o satır seçili kaldığı sürece BAŞKA hiçbir satırı da kaydedemez.
     */
    const kaldiriyor = Number.isFinite(h) && h === 0;
    const notWorkable = known && !kaldiriyor && (!meta?.workable || !dayHours || dayHours <= 0);
    const tooLong = known && !kaldiriyor && !notWorkable && Number.isFinite(h) && h > 0 && !!dayHours && h > dayHours;
    const hoursOk = hoursRangeOk && !tooLong;
    /** O günde şu an kayıtlı devamsızlık — "neyi siliyorum" görünür olsun. */
    const mevcutAbsence = g?.absenceHours ?? null;
    // Gün karşılığı: yalnız payda BİLİNİYORSA hesaplanır — tahmin edilmez.
    const dayFrac = known && dayHours && dayHours > 0 && Number.isFinite(h) ? Math.min(1, h / dayHours) : null;
    return {
      ...r, h, dateOk, hoursOk, hoursRangeOk, notWorkable, tooLong, known, kaldiriyor,
      dayHours, dayFrac, mevcutAbsence, statusLabel: meta?.label ?? g?.status ?? null,
      valid: dateOk && hoursOk && !notWorkable,
    };
  });
  const invalidCount = parsed.filter((p) => !p.valid).length;
  const overLimit = parsed.length > MAX_ROWS;
  const canSave = canManage && !locked && per.valid && parsed.length > 0 && invalidCount === 0 && !overLimit;

  const batchHours = parsed.reduce((s, p) => s + (p.valid ? p.h : 0), 0);
  const batchDays = parsed.reduce((s, p) => s + (p.valid && p.dayFrac !== null ? p.dayFrac : 0), 0);
  /** Gün karşılığı çözülemeyen satır sayısı — toplam "en az" olduğu için ilan edilir. */
  const unknownDays = parsed.filter((p) => p.valid && p.dayFrac === null).length;

  /**
   * O personelin AYLIK TOPLAM DEVAMSIZLIĞI — bu satır KAYDEDİLDİKTEN SONRAKİ hâliyle.
   * Uç aynı personel-gün için saati TOPLAMAZ, ÜZERİNE yazar; ay toplamı da o kuralı izler.
   */
  const monthAfterRow = (id: string, dateKey: string, newHours: number) => {
    const p = nameOf.get(id);
    const saved = p?.absenceHours ?? 0;
    const sameDay = (p?.days ?? []).find((x) => String(x.date).slice(0, 10) === dateKey)?.hours ?? 0;
    return Math.round((saved - sameDay + (Number.isFinite(newHours) ? newHours : 0)) * 100) / 100;
  };

  const bulkMut = trpc.hr.attendance.absenceBulk.useMutation({
    onSuccess: (res) => {
      qc.hr.attendance.invalidate();
      setPicked(new Map());
      pushToast({
        tone: res.warnings.length ? "warning" : "success",
        title: `${res.total} satır işlendi`,
        message: `${res.created} yeni · ${res.updated} güncellendi${res.cleared ? ` · ${res.cleared} kaldırıldı` : ""}${res.unchanged ? ` · ${res.unchanged} değişmedi` : ""}`,
        lines: res.warnings,
        duration: res.warnings.length ? 0 : undefined,
      });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message, duration: 0 }),
  });

  const save = () => {
    if (!canSave) return;
    bulkMut.mutate({ year, month, entries: parsed.map((p) => ({ employeeId: p.id, date: p.date, hours: p.h })) });
  };

  return (
    <>
      <PageFormShell
        open
        wide
        onClose={back}
        title={`Toplu Devamsızlık — ${per.label}`}
        subtitle="Personelleri seçin, her satıra tarih ve devamsızlık saatini yazın"
        footer={
          <>
            <span className="bulk-foot-sum" aria-live="polite">
              <b className="mono">{parsed.length}</b> satır · <b className="mono">{fmtH(batchHours)}</b> sa
              {batchDays > 0 ? <> · ≈ <b className="mono">{fmtD(batchDays)}</b> gün{unknownDays > 0 ? " (en az)" : ""}</> : null}
              {invalidCount > 0 ? <span className="bad"> · {invalidCount} satır hatalı</span> : null}
              {overLimit ? <span className="bad"> · en fazla {MAX_ROWS} satır</span> : null}
            </span>
            <button type="button" className="btn-md ghost" onClick={back}>Vazgeç</button>
            <button type="button" className="btn-md primary" disabled={!canSave || bulkMut.isPending} onClick={save}>
              <Icon name="check" size={14} />{bulkMut.isPending ? "Kaydediliyor…" : "Devamsızlıkları Kaydet"}
            </button>
          </>
        }
      >
        {/* ── Dönem ── */}
        <div className="bulk-period">
          <div className="form-field">
            <label className="lbl" htmlFor="ba-ym">Dönem (ay)</label>
            <input id="ba-ym" className="form-input" type="month" value={per.ym} onChange={(e) => per.setYm(e.target.value)} />
          </div>
          <span className="grow" />
        </div>
        {!per.valid ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="alertTriangle" size={14} />Geçerli bir ay seçin.
          </div>
        ) : null}
        {!canManage && !meQ.isLoading ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="alertTriangle" size={14} />Bu ekranda değişiklik yapma yetkiniz yok (manage:hr) — yalnız görüntüleyebilirsiniz.
          </div>
        ) : null}
        {locked && !statusQ.isLoading ? (
          <div className="form-banner-warn bulk-banner">
            <Icon name="lock" size={14} />
            Bu dönemin bordrosu <b>{statusQ.data?.payrollStatus === "paid" ? "ödenmiş" : "onaylanmış"}</b> — puantaj kilitli, devamsızlık girilemez.
          </div>
        ) : null}

        {/* ── Ayın mevcut durumu ── */}
        <div className="bulk-band">
          <div className="bulk-band-i">
            <span className="m-ovl">Bu ay devamsızlık girilen</span>
            <b className="mono">{monthQ.data ? `${monthQ.data.withAbsence} kişi` : "…"}</b>
          </div>
          <div className="bulk-band-i">
            <span className="m-ovl">Toplam devamsızlık</span>
            <b className="mono">{monthQ.data ? `${fmtH(monthQ.data.totalHours)} sa` : "…"}</b>
          </div>
          <div className="bulk-band-i">
            <span className="m-ovl">Gün karşılığı (bordroya giden)</span>
            <b className="mono neg">{monthQ.data ? `${fmtD(monthQ.data.totalDays)} gün` : "…"}</b>
          </div>
        </div>

        <div className="form-banner-info bulk-banner">
          <Icon name="info" size={14} />
          Saatlik devamsızlık yalnız <b>çalışılan güne</b> girilir ve o günün çalışma saatini aşamaz.
          Tam gün gelmeyen personel için Puantaj ekranından o günü <b>“Devamsız”</b> işaretleyin.
        </div>

        <div className="bulk-cols">
          {/* ── Sol: personel seçimi ── */}
          <section className="d-card bulk-pick">
            <div className="d-card-head">
              <div className="left"><span className="t">Personel</span><span className="s">{filtered.length} kişi</span></div>
              <span className="bulk-head-actions">
                <button type="button" className="bulk-link" onClick={selectAllShown}>Görünenleri seç</button>
                {picked.size > 0 ? <button type="button" className="bulk-link" onClick={() => setPicked(new Map())}>Temizle</button> : null}
              </span>
            </div>
            <div className="d-card-body">
              <input
                className="form-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ad, kod ya da pozisyon ara…"
                aria-label="Personel ara"
              />
            </div>
            <div className="d-card-body flush bulk-picklist">
              {monthQ.isLoading ? (
                <div className="mt-empty">Yükleniyor…</div>
              ) : monthQ.isError ? (
                <div className="mt-empty">{monthQ.error.message}</div>
              ) : filtered.length === 0 ? (
                <div className="mt-empty">Eşleşen personel yok.</div>
              ) : (
                <ul className="bulk-ul">
                  {filtered.map((p) => {
                    const on = picked.has(p.employeeId);
                    return (
                      <li key={p.employeeId}>
                        <label className={`bulk-li${on ? " on" : ""}`}>
                          <input type="checkbox" checked={on} onChange={() => toggle(p.employeeId)} />
                          <span className="bulk-li-main">
                            <span className="bulk-li-name">{p.name}</span>
                            <span className="bulk-li-sub">{p.positionTitle ?? "pozisyon yok"}</span>
                          </span>
                          <span className="bulk-li-fig">
                            <span className={p.absenceHours > 0 ? "bulk-li-h neg" : "bulk-li-dash"} title="Bu ay girilmiş toplam devamsızlık">
                              {p.absenceHours > 0 ? `${fmtH(p.absenceHours)} sa` : "—"}
                            </span>
                            <span className={p.absenceHours > 0 ? "bulk-sub" : "bulk-li-dash"} title="Bordroya giden gün karşılığı">
                              {p.absenceHours > 0 ? `${fmtD(p.absenceDays)} gün` : "—"}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          {/* ── Sağ: seçilenler + tarih/saat ── */}
          <section className="d-card bulk-rows">
            <div className="d-card-head">
              <div className="left"><span className="t">Seçilenler</span><span className="s">{rows.length} satır</span></div>
            </div>
            <div className="d-card-body bulk-apply">
              <div className="form-field">
                <label className="lbl" htmlFor="ba-ad">Tarih</label>
                <input id="ba-ad" className="form-input" type="date" min={per.min} max={per.max} value={applyDate} onChange={(e) => setApplyDate(e.target.value)} />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="ba-ah">Devamsızlık (saat)</label>
                <input id="ba-ah" className="form-input mono" inputMode="decimal" value={applyHours} onChange={(e) => setApplyHours(e.target.value)} placeholder="0" />
              </div>
              <button type="button" className="btn-md secondary" disabled={rows.length === 0 || (!applyDate && !applyHours)} onClick={spread}>
                <Icon name="copy" size={14} />Hepsine uygula
              </button>
            </div>
            {rows.length > PREVIEW_LIMIT ? (
              <div className="d-card-body">
                <div className="form-banner-warn bulk-inline-banner">
                  <Icon name="alertTriangle" size={14} />
                  Seçim {PREVIEW_LIMIT} kişiyi aştı — <b>gün önizlemesi kapalı</b>. Hafta sonu / tatil / izin günleri
                  kaydetme sırasında sunucu tarafından reddedilir.
                </div>
              </div>
            ) : null}
            {/* ÖNİZLEME HATASI YUTULMAZ: `gridBy` boş kalınca her satır "hesaplanmamış"
                görünüyor ve Kaydet açık kalıyordu — kullanıcı doğrulamanın çalıştığını
                sanarak 60 satırı sunucunun toplu reddine gönderiyordu. */}
            {previewOn && gridQ.isError ? (
              <div className="d-card-body">
                <div className="form-banner-err bulk-inline-banner" role="alert">
                  <Icon name="alertTriangle" size={14} />
                  <span>
                    Gün önizlemesi alınamadı — hangi günün çalışma günü olduğu <b>kontrol edilemiyor</b>.
                    Kaydetmeden önce listeyi yenileyin. ({gridQ.error.message})
                  </span>
                </div>
              </div>
            ) : null}
            <div className="d-card-body flush bulk-rowlist">
              {rows.length === 0 ? (
                <div className="mt-empty">Soldan personel seçin; seçtikleriniz burada satır olur.</div>
              ) : (
                <table className="dt2">
                  <thead>
                    <tr>
                      <th>Personel</th>
                      <th style={{ width: 148 }}>Tarih</th>
                      <th className="num" style={{ width: 110 }}>Devamsızlık (sa)</th>
                      <th style={{ width: 150 }}>O günün durumu</th>
                      <th className="num" style={{ width: 110 }} title="Bordronun kestiği birim: saat ÷ o günün çalışma saati">Gün karşılığı</th>
                      <th className="num" style={{ width: 120 }} title="Bu kayıttan sonra personelin o ayki TOPLAM devamsızlığı">Ay Toplamı</th>
                      <th style={{ width: 40 }} aria-label="Kaldır" />
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((r) => {
                      const p = nameOf.get(r.id);
                      return (
                        <tr key={r.id}>
                          <td>
                            {p?.name ?? r.id}
                            <span className="bulk-li-sub"> {p?.positionTitle ?? ""}</span>
                          </td>
                          <td>
                            <input
                              className={`form-input${r.dateOk && !r.notWorkable ? "" : " err"}`}
                              type="date"
                              min={per.min}
                              max={per.max}
                              value={r.date}
                              onChange={(e) => setRow(r.id, { date: e.target.value })}
                              aria-label={`${p?.name ?? ""} tarih`}
                              aria-invalid={!r.dateOk || r.notWorkable}
                              title={
                                !r.dateOk ? `Tarih ${per.label} dönemi içinde olmalı`
                                  : r.notWorkable ? `Bu gün çalışma günü değil (${r.statusLabel}) — saatlik devamsızlık girilemez`
                                    : undefined
                              }
                            />
                          </td>
                          <td>
                            <input
                              className={`form-input mono${r.hoursOk ? "" : " err"}`}
                              inputMode="decimal"
                              value={r.hours}
                              onChange={(e) => setRow(r.id, { hours: e.target.value })}
                              placeholder="0"
                              aria-label={`${p?.name ?? ""} devamsızlık saati`}
                              aria-invalid={!r.hoursOk}
                              title={
                                r.tooLong ? `Bu günün çalışma saati ${fmtH(r.dayHours ?? 0)} — daha fazlası girilemez`
                                  : r.hoursRangeOk ? undefined
                                    : "0 ile 24 arası bir saat girin (ör. 3 ya da 1,5)"
                              }
                            />
                          </td>
                          {/* DURUM = renk + İKON + METİN (CLAUDE.md ✅#5). Hata SEBEBİ
                              `title`da kalamaz: dokunmatikte hover yok, kullanıcı kırmızı
                              kutunun nedenini asla öğrenemiyordu. */}
                          <td>
                            {!r.dateOk ? (
                              <span className="bulk-cellnote">—</span>
                            ) : !previewOn ? (
                              <span className="bulk-cellnote">önizleme kapalı</span>
                            ) : gridQ.isError ? (
                              <span className="badge-pill b-warn" title={gridQ.error.message}>
                                <Icon name="alertTriangle" size={12} />önizleme alınamadı
                              </span>
                            ) : gridQ.isLoading ? (
                              <span className="bulk-cellnote">…</span>
                            ) : !r.known ? (
                              <span className="bulk-cellnote" title="Bu güne ait puantaj satırı yok — kaydederken vardiya planından çözülecek">
                                hesaplanmamış
                              </span>
                            ) : r.notWorkable ? (
                              <span className="badge-pill b-danger">
                                <Icon name="x" size={12} />{r.statusLabel} — girilemez
                              </span>
                            ) : r.tooLong ? (
                              <span className="badge-pill b-warn">
                                <Icon name="alertTriangle" size={12} />en çok {fmtH(r.dayHours ?? 0)} sa
                              </span>
                            ) : r.kaldiriyor && (r.mevcutAbsence ?? 0) > 0 ? (
                              <span className="badge-pill b-warn" title="Saat 0 → bu günün devamsızlığı kaldırılacak">
                                <Icon name="x" size={12} />{fmtH(r.mevcutAbsence!)} sa kaldırılacak
                              </span>
                            ) : (
                              <span className="badge-pill b-success" title={`Çalışma saati ${fmtH(r.dayHours ?? 0)} sa`}>
                                <Icon name="check" size={12} />{r.statusLabel}
                              </span>
                            )}
                          </td>
                          <td className="cell-num">
                            {r.dayFrac === null ? (
                              <span className="bulk-dash">—</span>
                            ) : (
                              <b className="mono bulk-dayfrac">{fmtD(r.dayFrac)} gün</b>
                            )}
                          </td>
                          <td className="cell-num bulk-month">
                            <span className="bulk-sub">{fmtH(monthAfterRow(r.id, r.date, r.h))} sa</span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="iact danger"
                              title="Satırı kaldır"
                              aria-label={`${p?.name ?? ""} satırını kaldır`}
                              onClick={() => toggle(r.id)}
                            >
                              <Icon name="x" size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>

        <p className="bulk-note">
          Aynı personel-gün için saat <b>üzerine yazılır</b>, toplanmaz — listeyi iki kez kaydetmek çift kesinti oluşturmaz.
          Saat <b>0</b> girilirse o günün devamsızlığı kaldırılır. Girilen devamsızlık “Puantaj Hesapla” ile <b>silinmez</b>.
        </p>
      </PageFormShell>
      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );
}
